// ./context/AuthContext.tsx
import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  useRef,
  useCallback,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthService } from '../services/auth.service';
import { useShoppingCart } from './ShoppingCartContext';
import { jwtDecode } from 'jwt-decode';

interface AuthContextType {
  isAuthenticated: boolean;
  isValidated: boolean;
  login: (
    username: string,
    password: string,
    languageISO2: string | undefined,
  ) => Promise<void>;
  logout: () => void;
  token: string | null;
}

interface JwtPayload {
  exp: number;
}

export const AuthContext = createContext<AuthContextType | undefined>(
  undefined,
);

export const AuthContextProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [auth, setAuth] = useState<boolean>(AuthService.IsAuthenticated());
  const [validated, setValidated] = useState<boolean>(false);
  const [token, setToken] = useState<string | null>(AuthService.GetAuthToken());
  const navigate = useNavigate();
  const { emptyCart } = useShoppingCart();
  const logoutTimer = useRef<NodeJS.Timeout | null>(null);

  const handleLogin = async (
    username: string,
    password: string,
    languageISO2: string | undefined,
  ) => {
    try {
      languageISO2 = languageISO2 ?? 'en';
      const token = await AuthService.Login(username, password, languageISO2);
      await AuthService.ValidateEmail(languageISO2);
      const role = AuthService.GetRole();
      if (token && role) {
        setAuth(true);
        setValidated(true);
        setToken(token);
        navigate('/all-products');
      } else {
        setAuth(false);
        setToken(null);
      }
    } catch (error) {
      console.log(error);
      AuthService.LogOut();
      throw error;
    }
  };

  const handleLogout = useCallback(() => {
    AuthService.LogOut();
    setAuth(false);
    setToken(null);
    emptyCart();
    navigate('/');
    clearLogoutTimer();
  }, [emptyCart, navigate]);

  const clearLogoutTimer = () => {
    if (logoutTimer.current) {
      clearTimeout(logoutTimer.current);
      logoutTimer.current = null;
    }
  };

  const scheduleLogout = useCallback(
    (token: string | null) => {
      clearLogoutTimer();
      if (!token) return;

      try {
        const decoded = jwtDecode<JwtPayload>(token);
        if (decoded.exp) {
          const expirationTime = decoded.exp * 1000;
          const timeLeft = expirationTime - Date.now();
          if (timeLeft > 0) {
            logoutTimer.current = setTimeout(() => {
              handleLogout();
            }, timeLeft);
          } else {
            handleLogout();
          }
        }
      } catch (e) {
        handleLogout();
      }
    },
    [handleLogout],
  );

  useEffect(() => {
    setAuth(AuthService.IsAuthenticated());
    setToken(AuthService.GetAuthToken());
  }, []);

  useEffect(() => {
    scheduleLogout(token);
  }, [scheduleLogout, token]);

  return (
    <AuthContext.Provider
      value={{
        isValidated: validated,
        isAuthenticated: auth,
        login: handleLogin,
        logout: handleLogout,
        token,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe usarse dentro de un AuthContextProvider');
  }
  return context;
};
